/**
 * Cashflow (Overall > Finance), Fan 2026-10-22: month-by-month cash in/out
 * for the whole group with a plain explanation of WHY each month went up or
 * down. Data = GET /reports/cashflow-analysis (services/cashflow_analysis.py),
 * same basis as the Overall dashboard's Monthly Cashflow chart, so the bars
 * here equal the bars there. The explanation sentences are built server-side
 * from the numbers (no model), so every figure on screen is traceable.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ReferenceLine,
} from 'recharts';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { reportsAPI } from '../lib/api';
import { formatCompact } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

const POS = '#10B981';
const NEG = '#DC2626';
const PARTIAL = '#94A3B8';
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function num(v) {
  var n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function gbp(v) {
  var n = num(v);
  var s = Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits: 0 });
  return (n < 0 ? '-' : '') + '£' + s;
}

function Line2({ label, value, sub, strong }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4 py-1.5 border-b border-border/50 last:border-0', strong && 'font-semibold')}>
      <div className="min-w-0">
        <span className="text-sm">{label}</span>
        {sub && <span className="ml-2 text-xs text-muted-foreground">{sub}</span>}
      </div>
      <span className={cn('text-sm tabular-nums whitespace-nowrap', num(value) < 0 ? 'text-red-600' : 'text-emerald-700')}>
        {gbp(value)}
      </span>
    </div>
  );
}

export function CashflowPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(function () {
    var alive = true;
    setLoading(true);
    setError(null);
    reportsAPI.getCashflowAnalysis(year)
      .then(function (res) {
        if (!alive) return;
        setData(res.data);
        var months = res.data.months || [];
        // Default: the latest complete month that went negative, else the latest month.
        var neg = months.filter(function (m) { return num(m.net) < 0 && !m.incomplete; });
        var pick = neg.length ? neg[neg.length - 1] : months[months.length - 1];
        setSelected(pick ? pick.month : null);
      })
      .catch(function (err) { if (alive) setError(getErrorMessage(err)); })
      .finally(function () { if (alive) setLoading(false); });
    return function () { alive = false; };
  }, [year]);

  const chartData = useMemo(function () {
    return ((data && data.months) || []).map(function (m) {
      return { key: m.month, label: SHORT[parseInt(m.month.slice(5, 7), 10) - 1], net: num(m.net), inProgress: m.in_progress, incomplete: !!m.incomplete };
    });
  }, [data]);

  const month = useMemo(function () {
    return ((data && data.months) || []).find(function (m) { return m.month === selected; }) || null;
  }, [data, selected]);

  const latestGaps = (((data && data.months) || []).slice(-1)[0] || {}).missing_feeds
    ? data.months.slice(-1)[0].missing_feeds.length : 0;

  // Moves between our own accounts and Spiko are not money in or out (Fan, 2026-10-22).
  const isTransfer = function (d) { return d.key === 'own_transfers' || d.key === 'savings'; };
  const moneyIn = month ? (month.drivers || []).filter(function (d) { return num(d.money_in) > 0 && !isTransfer(d); })
    .sort(function (a, b) { return num(b.money_in) - num(a.money_in); }) : [];
  const moneyOut = month ? (month.drivers || []).filter(function (d) { return num(d.money_out) < 0 && !isTransfer(d); })
    .sort(function (a, b) { return num(a.money_out) - num(b.money_out); }) : [];
  const transfers = month ? (month.drivers || []).filter(isTransfer) : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Cashflow</h1>
            <p className="text-muted-foreground mt-1">
              Money in and out of all bank accounts, month by month, and why. Click a month to see the reasons.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={function () { setYear(year - 1); }} disabled={year <= 2026} aria-label="Previous year">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium w-12 text-center">{year}</span>
            <Button variant="outline" size="icon" onClick={function () { setYear(year + 1); }} disabled={year >= currentYear} aria-label="Next year">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <Loader2 className="h-6 w-6 animate-spin" /> Working out the cashflow, this can take a few seconds
          </div>
        )}
        {!loading && error && (
          <Card><CardContent className="pt-6 flex items-center gap-2 text-red-600"><AlertTriangle className="h-4 w-4" />{error}</CardContent></Card>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card><CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Net cash flow {year} to date</p>
                <p className={cn('text-2xl font-bold', num(data.net_ytd) < 0 ? 'text-red-600' : 'text-emerald-700')}>{gbp(data.net_ytd)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Months with complete bank data</p>
                <p className="text-2xl font-bold">{(data.months || []).length - (data.incomplete_months || []).length} of {(data.months || []).length}</p>
                {(data.incomplete_months || []).length > 0 && (
                  <p className="text-xs text-amber-700 mt-1">Grey bars are missing a bank account, so they are not the real result.</p>
                )}
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Bank accounts with missing data (latest month)</p>
                <p className="text-2xl font-bold">{latestGaps}</p>
              </CardContent></Card>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Net cash flow by month</CardTitle>
                <p className="text-xs text-muted-foreground">{data.basis}</p>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 24, right: 12, left: 12, bottom: 4 }}
                      onClick={function (e) { if (e && e.activePayload && e.activePayload[0]) setSelected(e.activePayload[0].payload.key); }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={function (v) { return formatCompact(v, 'GBP'); }} width={70} />
                      <ReferenceLine y={0} stroke="#94a3b8" />
                      <Tooltip formatter={function (v, n, item) {
                        return [gbp(v) + (item && item.payload && item.payload.incomplete ? ' (incomplete data)' : ''), 'Net cash flow'];
                      }} />
                      <Bar dataKey="net" cursor="pointer" radius={[3, 3, 0, 0]}>
                        {chartData.map(function (r) {
                          return (
                            <Cell key={r.key} fill={r.incomplete ? PARTIAL : (r.net < 0 ? NEG : POS)}
                              fillOpacity={r.key === selected ? 1 : 0.55}
                              stroke={r.key === selected ? '#0f172a' : 'none'} strokeWidth={r.key === selected ? 1.5 : 0} />
                          );
                        })}
                        <LabelList dataKey="net" position="top" formatter={function (v) { return formatCompact(v, 'GBP'); }} style={{ fontSize: 11 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {chartData.map(function (r) {
                    return (
                      <button key={r.key} onClick={function () { setSelected(r.key); }}
                        className={cn('px-2.5 py-1 rounded-md text-xs border',
                          r.key === selected ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted',
                          r.net < 0 && !r.incomplete && r.key !== selected && 'text-red-600',
                          r.incomplete && r.key !== selected && 'text-muted-foreground')}>
                        {r.label}{r.inProgress ? ' (so far)' : ''}{r.incomplete ? ' *' : ''}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {month && (
              <>
                <Card className={cn(month.incomplete ? 'border-amber-300' : (num(month.net) < 0 ? 'border-red-200' : 'border-emerald-200'))}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {month.incomplete
                        ? <AlertTriangle className="h-5 w-5 text-amber-600" />
                        : (num(month.net) < 0
                          ? <ArrowDownRight className="h-5 w-5 text-red-600" />
                          : <ArrowUpRight className="h-5 w-5 text-emerald-600" />)}
                      {month.label}: {gbp(month.net)}{month.in_progress ? ' so far' : ''}
                      {month.incomplete && (
                        <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Incomplete data</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {month.profit !== null && month.profit !== undefined && (
                      <div className="mb-3 grid grid-cols-2 gap-3 max-w-md">
                        <div className="rounded-md border p-2">
                          <p className="text-xs text-muted-foreground">Cash in minus out</p>
                          <p className={cn('text-lg font-semibold tabular-nums', num(month.net) < 0 ? 'text-red-600' : 'text-emerald-700')}>{gbp(month.net)}</p>
                        </div>
                        <div className="rounded-md border p-2">
                          <p className="text-xs text-muted-foreground">Profit (P&amp;L)</p>
                          <p className={cn('text-lg font-semibold tabular-nums', num(month.profit) < 0 ? 'text-red-600' : 'text-emerald-700')}>{gbp(month.profit)}</p>
                        </div>
                      </div>
                    )}
                    <p className="text-sm font-medium mb-2">Why</p>
                    <ul className="space-y-1.5 text-sm list-disc pl-5">
                      {(month.explanation || []).map(function (s, i) { return <li key={i}>{s}</li>; })}
                    </ul>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Money in: {gbp(month.money_in)}</CardTitle></CardHeader>
                    <CardContent>
                      {moneyIn.map(function (d) {
                        return <Line2 key={d.key} label={d.label} value={d.money_in} />;
                      })}
                      {moneyIn.length === 0 && <p className="text-sm text-muted-foreground">Nothing came in.</p>}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Money out: {gbp(month.money_out)}</CardTitle></CardHeader>
                    <CardContent>
                      {moneyOut.map(function (d) {
                        return <Line2 key={d.key} label={d.label} value={d.money_out} />;
                      })}
                      {moneyOut.length === 0 && <p className="text-sm text-muted-foreground">Nothing went out.</p>}
                    </CardContent>
                  </Card>
                </div>

                {transfers.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Moved between our own accounts</CardTitle>
                      <p className="text-xs text-muted-foreground">Not counted as money in or out. Only a difference that does not net to zero affects the month.</p>
                    </CardHeader>
                    <CardContent>
                      {transfers.map(function (d) {
                        return (
                          <Line2 key={d.key} label={d.label}
                            sub={'out ' + gbp(Math.abs(num(d.money_out))) + ', back in ' + gbp(d.money_in)}
                            value={d.net} />
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">By business line</CardTitle></CardHeader>
                    <CardContent>
                      {(month.business_lines || []).map(function (l) {
                        return <Line2 key={l.key} label={l.label} value={l.net} />;
                      })}
                      <Line2 label="Total" value={month.net} strong />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Largest payments out</CardTitle></CardHeader>
                    <CardContent>
                      {(month.largest_out || []).map(function (m) {
                        var native = m.currency !== 'GBP'
                          ? Math.abs(num(m.amount)).toLocaleString('en-GB', { maximumFractionDigits: 0 }) + ' ' + m.currency
                          : null;
                        return (
                          <Line2 key={m.id || (m.date + m.who)} label={m.who || m.driver_label}
                            sub={m.date.slice(8, 10) + ' ' + SHORT[parseInt(m.date.slice(5, 7), 10) - 1] + (native ? ', ' + native : '')}
                            value={m.amount_gbp} />
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>

                {(month.missing_feeds || []).length > 0 && (
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        Bank data missing for this month
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        These accounts have no transactions for all or part of this month, so the month is not complete.
                      </p>
                    </CardHeader>
                    <CardContent>
                      {month.missing_feeds.map(function (g) {
                        return (
                          <div key={g.account + g.entity} className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0 text-sm">
                            <span>{g.account} <span className="text-muted-foreground">({g.entity})</span></span>
                            <span className="text-muted-foreground whitespace-nowrap">
                              {g.kind === 'not_started' ? 'no data before ' + g.first_transaction : 'last data ' + g.last_transaction}
                            </span>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
